import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Slider,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  Fade,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import {
  Close,
  PlayArrow,
  Pause,
  SkipNext,
  SkipPrevious,
  VolumeUp,
  VolumeOff,
  Fullscreen,
  FullscreenExit,
  Shuffle,
  Repeat,
  RepeatOne,
  Stop,
  Settings,
  ViewList,
  Image as ImageIcon,
  VideoFile,
  AudioFile,
  Article,
  Web,
} from '@mui/icons-material';

import type { Playlist, PlaylistItem, Media, MediaType } from '../../types';
import apiClient from '../../services/api';
import { convertMediaUrl } from '../../config/environment';

interface PlaylistPreviewDialogProps {
  open: boolean;
  playlist: Playlist | null;
  onClose: () => void;
}

interface PlaylistItemWithMedia extends PlaylistItem {
  media: Media;
}

interface PlaybackState {
  isPlaying: boolean;
  currentItemIndex: number;
  currentTime: number;
  duration: number;
  volume: number;
  muted: boolean;
  shuffle: boolean;
  repeat: 'none' | 'all' | 'one';
  fullscreen: boolean;
}

const PlaylistPreviewDialog: React.FC<PlaylistPreviewDialogProps> = ({
  open,
  playlist,
  onClose,
}) => {
  const { t } = useTranslation();

  // Состояние
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<PlaylistItemWithMedia[]>([]);
  const [playbackState, setPlaybackState] = useState<PlaybackState>({
    isPlaying: false,
    currentItemIndex: 0,
    currentTime: 0,
    duration: 0,
    volume: 1,
    muted: false,
    shuffle: false,
    repeat: 'none',
    fullscreen: false,
  });
  const [showControls, setShowControls] = useState(true);
  const [controlsTimer, setControlsTimer] = useState<NodeJS.Timeout | null>(null);

  // Refs для медиа элементов
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Загрузка элементов плейлиста
  const loadPlaylistItems = useCallback(async () => {
    if (!playlist?.id) return;

    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Загружаю элементы плейлиста для предпросмотра:', playlist.id);
      const response = await apiClient.getPlaylistItems(playlist.id);

      // Фильтруем только элементы с медиафайлами
      const itemsWithMedia = response.filter((item: PlaylistItem) => item.media) as PlaylistItemWithMedia[];

      console.log('✅ Загружено элементов для предпросмотра:', itemsWithMedia.length);
      setItems(itemsWithMedia);

      // Сбрасываем индекс если нет элементов
      if (itemsWithMedia.length === 0) {
        setPlaybackState(prev => ({ ...prev, currentItemIndex: 0 }));
      }
    } catch (error) {
      console.error('❌ Ошибка загрузки элементов плейлиста:', error);
      setError('Ошибка загрузки элементов плейлиста');
    } finally {
      setLoading(false);
    }
  }, [playlist?.id]);

  // Инициализация при открытии диалога
  useEffect(() => {
    if (open && playlist) {
      loadPlaylistItems();
      setPlaybackState(prev => ({
        ...prev,
        shuffle: playlist.shuffle_enabled || false,
        repeat: playlist.loop_enabled ? 'all' : 'none',
      }));
    }
  }, [open, playlist, loadPlaylistItems]);

  // Текущий элемент
  const currentItem = items[playbackState.currentItemIndex];
  const currentMedia = currentItem?.media;

  // Эффективная длительность элемента (с учетом duration_override)
  const getEffectiveDuration = useCallback((item: PlaylistItemWithMedia): number => {
    if (item.duration_override && item.duration_override > 0) {
      return item.duration_override;
    }

    if (item.media.duration && item.media.duration > 0) {
      return item.media.duration;
    }

    // Дефолтная длительность для изображений
    if (item.media.type === 'image') {
      return 10; // 10 секунд
    }

    return 30; // Дефолтная длительность
  }, []);

  // Переход к следующему элементу
  const goToNextItem = useCallback(() => {
    if (items.length === 0) return;

    let nextIndex;
    if (playbackState.shuffle) {
      // Случайный выбор (исключая текущий)
      const availableIndices = items.map((_, i) => i).filter(i => i !== playbackState.currentItemIndex);
      nextIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)] || 0;
    } else {
      // Последовательный переход
      nextIndex = (playbackState.currentItemIndex + 1) % items.length;
    }

    setPlaybackState(prev => ({
      ...prev,
      currentItemIndex: nextIndex,
      currentTime: 0,
    }));
  }, [items.length, playbackState.currentItemIndex, playbackState.shuffle]);

  // Переход к предыдущему элементу
  const goToPreviousItem = useCallback(() => {
    if (items.length === 0) return;

    const prevIndex = playbackState.currentItemIndex === 0
      ? items.length - 1
      : playbackState.currentItemIndex - 1;

    setPlaybackState(prev => ({
      ...prev,
      currentItemIndex: prevIndex,
      currentTime: 0,
    }));
  }, [items.length, playbackState.currentItemIndex]);

  // Воспроизведение/пауза
  const togglePlayback = useCallback(() => {
    if (!currentMedia) return;

    if (currentMedia.type === 'video' && videoRef.current) {
      if (playbackState.isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    } else if (currentMedia.type === 'audio' && audioRef.current) {
      if (playbackState.isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    } else if (currentMedia.type === 'image') {
      // Для изображений просто переключаем состояние
      setPlaybackState(prev => ({ ...prev, isPlaying: !prev.isPlaying }));

      if (!playbackState.isPlaying) {
        // Запускаем таймер для изображения
        const duration = getEffectiveDuration(currentItem) * 1000;
        imageTimerRef.current = setTimeout(() => {
          if (playbackState.repeat === 'one') {
            // Повторяем текущий элемент
            setPlaybackState(prev => ({ ...prev, currentTime: 0 }));
          } else {
            goToNextItem();
          }
        }, duration);
      } else {
        // Останавливаем таймер
        if (imageTimerRef.current) {
          clearTimeout(imageTimerRef.current);
          imageTimerRef.current = null;
        }
      }
    }

    setPlaybackState(prev => ({ ...prev, isPlaying: !prev.isPlaying }));
  }, [currentMedia, playbackState.isPlaying, currentItem, getEffectiveDuration, goToNextItem, playbackState.repeat]);

  // Остановка воспроизведения
  const stopPlayback = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (imageTimerRef.current) {
      clearTimeout(imageTimerRef.current);
      imageTimerRef.current = null;
    }

    setPlaybackState(prev => ({
      ...prev,
      isPlaying: false,
      currentTime: 0,
    }));
  }, []);

  // Обработка событий видео/аудио
  const handleMediaTimeUpdate = useCallback(() => {
    if (currentMedia?.type === 'video' && videoRef.current) {
      setPlaybackState(prev => ({
        ...prev,
        currentTime: videoRef.current!.currentTime,
        duration: videoRef.current!.duration || 0,
      }));
    } else if (currentMedia?.type === 'audio' && audioRef.current) {
      setPlaybackState(prev => ({
        ...prev,
        currentTime: audioRef.current!.currentTime,
        duration: audioRef.current!.duration || 0,
      }));
    }
  }, [currentMedia?.type]);

  // Обработка окончания воспроизведения
  const handleMediaEnded = useCallback(() => {
    if (playbackState.repeat === 'one') {
      // Повторяем текущий элемент
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      } else if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
      }
    } else if (playbackState.repeat === 'all' || playbackState.currentItemIndex < items.length - 1) {
      // Переходим к следующему элементу
      goToNextItem();
    } else {
      // Останавливаем воспроизведение
      stopPlayback();
    }
  }, [playbackState.repeat, playbackState.currentItemIndex, items.length, goToNextItem, stopPlayback]);

  // Скрытие контролов при бездействии
  const hideControlsTimer = useCallback(() => {
    if (controlsTimer) {
      clearTimeout(controlsTimer);
    }

    const timer = setTimeout(() => {
      if (playbackState.isPlaying) {
        setShowControls(false);
      }
    }, 3000); // Скрываем через 3 секунды

    setControlsTimer(timer);
  }, [controlsTimer, playbackState.isPlaying]);

  // Показ контролов при движении мыши
  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    hideControlsTimer();
  }, [hideControlsTimer]);

  // Полноэкранный режим
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;

    if (!playbackState.fullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }

    setPlaybackState(prev => ({ ...prev, fullscreen: !prev.fullscreen }));
  }, [playbackState.fullscreen]);

  // Закрытие предпросмотра
  const handleClose = useCallback(() => {
    stopPlayback();
    if (playbackState.fullscreen && document.exitFullscreen) {
      document.exitFullscreen();
    }
    onClose();
  }, [stopPlayback, playbackState.fullscreen, onClose]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (imageTimerRef.current) {
        clearTimeout(imageTimerRef.current);
      }
      if (controlsTimer) {
        clearTimeout(controlsTimer);
      }
    };
  }, [controlsTimer]);

  // Рендер иконки типа медиа
  const getMediaTypeIcon = (type: MediaType) => {
    switch (type) {
      case 'video': return <VideoFile />;
      case 'image': return <ImageIcon />;
      case 'audio': return <AudioFile />;
      case 'pdf': return <Article />;
      case 'html': return <Web />;
      default: return <VideoFile />;
    }
  };

  // Рендер плеера медиафайла
  const renderMediaPlayer = () => {
    if (!currentMedia) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
          bgcolor="black"
        >
          <Typography variant="h6" color="white">
            {t('playlists.noItems')}
          </Typography>
        </Box>
      );
    }

    const commonStyle = {
      width: '100%',
      height: '100%',
      objectFit: 'contain' as const,
    };
    const mediaUrl = convertMediaUrl(currentMedia.file_url);

    switch (currentMedia.type) {
      case 'video':
        return (
          <video
            ref={videoRef}
            src={mediaUrl}
            style={commonStyle}
            onTimeUpdate={handleMediaTimeUpdate}
            onEnded={handleMediaEnded}
            onLoadedData={() => {
              if (playbackState.isPlaying && videoRef.current) {
                videoRef.current.play();
              }
            }}
            muted={playbackState.muted}
            volume={playbackState.volume}
          />
        );

      case 'image':
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            bgcolor="black"
          >
            <img
              src={mediaUrl}
              alt={currentMedia.title}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        );

      case 'audio':
        return (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            bgcolor="black"
            color="white"
            p={4}
          >
            <audio
              ref={audioRef}
              src={mediaUrl}
              onTimeUpdate={handleMediaTimeUpdate}
              onEnded={handleMediaEnded}
              onLoadedData={() => {
                if (playbackState.isPlaying && audioRef.current) {
                  audioRef.current.play();
                }
              }}
              volume={playbackState.volume}
              muted={playbackState.muted}
            />

            {/* Визуализация аудио */}
            <AudioFile sx={{ fontSize: 120, mb: 2, opacity: 0.7 }} />
            <Typography variant="h4" textAlign="center" gutterBottom>
              {currentMedia.title}
            </Typography>
            <Typography variant="body1" textAlign="center" opacity={0.7}>
              {t('media.types.audio')}
            </Typography>
          </Box>
        );

      case 'pdf':
        return (
          <Box height="100%" bgcolor="white">
            <iframe
              src={mediaUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={currentMedia.title}
            />
          </Box>
        );

      case 'html':
        return (
          <Box height="100%" bgcolor="white">
            <iframe
              src={mediaUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={currentMedia.title}
              sandbox="allow-scripts allow-same-origin"
            />
          </Box>
        );

      default:
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            bgcolor="black"
            color="white"
          >
            <Typography variant="h6">
              {t('media.previewNotAvailable')}
            </Typography>
          </Box>
        );
    }
  };

  // Рендер контролов
  const renderControls = () => (
    <Fade in={showControls}>
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        bgcolor="rgba(0, 0, 0, 0.8)"
        color="white"
        p={2}
      >
        {/* Прогресс бар */}
        <Box mb={2}>
          <LinearProgress
            variant="determinate"
            value={
              playbackState.duration > 0
                ? (playbackState.currentTime / playbackState.duration) * 100
                : 0
            }
            sx={{
              height: 6,
              bgcolor: 'rgba(255, 255, 255, 0.3)',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'primary.main',
              },
            }}
          />
        </Box>

        {/* Информация о текущем элементе */}
        <Box display="flex" alignItems="center" mb={2}>
          {getMediaTypeIcon(currentMedia?.type || 'video')}
          <Box ml={1} flex={1}>
            <Typography variant="body1" noWrap>
              {currentMedia?.title || ''}
            </Typography>
            <Typography variant="body2" opacity={0.7} noWrap>
              {playbackState.currentItemIndex + 1} / {items.length} • {currentMedia?.type || ''}
            </Typography>
          </Box>
        </Box>

        {/* Элементы управления */}
        <Stack direction="row" alignItems="center" spacing={1}>
          {/* Предыдущий */}
          <IconButton
            color="inherit"
            onClick={goToPreviousItem}
            disabled={items.length <= 1}
          >
            <SkipPrevious />
          </IconButton>

          {/* Play/Pause */}
          <IconButton
            color="inherit"
            onClick={togglePlayback}
            size="large"
          >
            {playbackState.isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>

          {/* Стоп */}
          <IconButton
            color="inherit"
            onClick={stopPlayback}
          >
            <Stop />
          </IconButton>

          {/* Следующий */}
          <IconButton
            color="inherit"
            onClick={goToNextItem}
            disabled={items.length <= 1}
          >
            <SkipNext />
          </IconButton>

          <Box flex={1} />

          {/* Громкость */}
          <IconButton
            color="inherit"
            onClick={() => setPlaybackState(prev => ({ ...prev, muted: !prev.muted }))}
          >
            {playbackState.muted ? <VolumeOff /> : <VolumeUp />}
          </IconButton>

          {/* Перемешивание */}
          <IconButton
            color={playbackState.shuffle ? 'primary' : 'inherit'}
            onClick={() => setPlaybackState(prev => ({ ...prev, shuffle: !prev.shuffle }))}
          >
            <Shuffle />
          </IconButton>

          {/* Повтор */}
          <IconButton
            color={playbackState.repeat !== 'none' ? 'primary' : 'inherit'}
            onClick={() => {
              const nextRepeat = playbackState.repeat === 'none'
                ? 'all'
                : playbackState.repeat === 'all'
                  ? 'one'
                  : 'none';
              setPlaybackState(prev => ({ ...prev, repeat: nextRepeat }));
            }}
          >
            {playbackState.repeat === 'one' ? <RepeatOne /> : <Repeat />}
          </IconButton>

          {/* Полноэкранный режим */}
          <IconButton
            color="inherit"
            onClick={toggleFullscreen}
          >
            {playbackState.fullscreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>

          {/* Закрыть */}
          <IconButton
            color="inherit"
            onClick={handleClose}
          >
            <Close />
          </IconButton>
        </Stack>
      </Box>
    </Fade>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'black',
          color: 'white',
        },
      }}
    >
      <DialogContent
        ref={containerRef}
        sx={{
          p: 0,
          height: '100vh',
          position: 'relative',
          cursor: showControls ? 'default' : 'none',
        }}
        onMouseMove={handleMouseMove}
      >
        {loading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <CircularProgress color="primary" size={60} />
          </Box>
        ) : error ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            p={4}
          >
            <Alert severity="error">
              {error}
            </Alert>
          </Box>
        ) : (
          <>
            {renderMediaPlayer()}
            {renderControls()}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlaylistPreviewDialog;
